import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useCreateBrowserExtension, useUpdateBrowserExtension } from "@/api/devices";
import type { DeviceBrowserExtension } from "@/api/types";

const BROWSERS = ["CHROME", "FIREFOX", "OPERA", "SAFARI", "EDGE"] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  extension?: DeviceBrowserExtension | null;
}

export function BrowserExtensionModal({ open, onClose, tenantId, deviceId, extension }: Props) {
  const isEdit = !!extension;
  const create = useCreateBrowserExtension(tenantId, deviceId);
  const update = useUpdateBrowserExtension(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [extensionId, setExtensionId] = useState("");
  const [name, setName] = useState("");
  const [browser, setBrowser] = useState<DeviceBrowserExtension["browser"]>("CHROME");

  useEffect(() => {
    if (open) {
      setExtensionId(extension?.extension_id ?? "");
      setName(extension?.name ?? "");
      setBrowser(extension?.browser ?? "CHROME");
    }
  }, [open, extension]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { extension_id: extensionId, name, browser };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: extension.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Browser Extension</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Name *</label>
          <Input value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Extension ID *</label>
          <Input value={extensionId} onChange={e => setExtensionId(e.target.value)} required />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Browser *</label>
          <Select value={browser} onChange={e => setBrowser(e.target.value as DeviceBrowserExtension["browser"])}>
            {BROWSERS.map(b => <option key={b} value={b}>{b.charAt(0) + b.slice(1).toLowerCase()}</option>)}
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending || !name || !extensionId}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
